import { datadogRum } from '@datadog/browser-rum-slim';
import { addToCartEvent } from '@portal/analytics/events/buildBundle';
import { selectProductEvent } from '@portal/analytics/events/studio';
import { CartDetails, CartInput } from '@portal/api/shopifyV2/types';
import { SHOPIFY_STORE_BRAND } from '@portal/config/base';
import { MONITORING_TAGS } from '@portal/constants/monitoring';
import { useSiteCart } from '@portal/shared/components/SiteCart/hooks/SiteCartContext';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { isMobileTablet } from '@portal/shared/utils/devices';
import { safeLocalStorage } from '@portal/shared/utils/safeLocalStorage';
import { isOTPpacksVariantV53 } from '@portal/shared/utils/shopify/isOTPpacksVariantV53';
import { isOTPpouchVariant } from '@portal/shared/utils/shopify/isOTPpouchVariant';
import { isStandaloneProduct } from '@portal/shared/utils/shopify/isStandaloneProduct';
import { CHECKOUT_TYPE } from '@portal/state/cart/constants';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { getTrackingParams } from '@portal/state/localStorage/trackingParams';
import { useProductStore } from '@portal/state/store/pdpStore';
import { useGateValue } from '@statsig/react-bindings';
import { useSessionValue } from 'appLayer/auth';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useShopifyCartUrl } from 'shared/hooks/useShopifyCartUrl';
import { getFormattedShopifyId, getIdFromShopifyFormattedId } from 'shared/utils/shopify/ids';
import { SSGates } from 'state/context/StatsigContext/StatsigFlags';
import cookies from 'js-cookie';
import { getSelectedVariant, getSubscriptionLineItems, getUniqueVariantId } from './utils';

export const usePdpCheckout = () => {
  const store = useStoreValue();
  const router = useRouter();
  const session = useSessionValue();
  const locale = useLocale();
  const { setCheckoutLoading, selectedVariantId, isOtpProduct, productCadence, quantity, flavor } =
    useProductStore();
  const variants = useProductStore((state) => state.variants);
  const defaultVariantId = variants && variants[0] ? getUniqueVariantId(variants[0], flavor) : '';
  const trackingParams = getTrackingParams();
  const pageSlug = router.asPath;
  const isMobileTabletDevice = isMobileTablet();
  const { getShopifyCartUrl } = useShopifyCartUrl();

  const { addLinesToCart, openCart } = useSiteCart();

  const enableOpenCart = useGateValue(SSGates.enableShopAllAddToCart);

  const utmString = Object.keys(trackingParams)
    .filter((key) => key.startsWith('utm_'))
    .map((key) => `${key}=${trackingParams[key]}`)
    .join('&');

  const formattedVariantId = selectedVariantId || defaultVariantId;

  const selectedVariant = useMemo(
    () => getSelectedVariant(variants, formattedVariantId, store, flavor),
    [variants, formattedVariantId, flavor, store]
  );

  const getGiftProducts = useCallback(() => {
    // Only include gift products for subscription purchases, not OTPs
    if (isOtpProduct) {
      return [];
    }

    if (selectedVariant?.gifts && selectedVariant.gifts.length) {
      const giftProducts = selectedVariant.gifts
        .filter((gift: any) => gift.targetStore === store)
        .filter((gift: any) => gift.amount > 0)
        .map((gift: any) => ({
          merchandiseId: getFormattedShopifyId('ProductVariant', gift.variantId),
          quantity: gift.amount
        }));

      return giftProducts;
    }

    return [];
  }, [selectedVariant, store, isOtpProduct]);

  const handleCreatePdpCheckout = useCallback(async () => {
    const sellingPlanToSubmit = isOtpProduct
      ? 'otp'
      : productCadence || selectedVariant?.subscriptionVariantIds?.[0].sellingPlanId;

    const variantIdToSubmit =
      sellingPlanToSubmit === 'otp' && selectedVariant?.oneTimeVariantId
        ? selectedVariant?.oneTimeVariantId[0]?.variantId
        : selectedVariant?.subscriptionVariantIds[0]?.variantId;

    if (!variantIdToSubmit || !sellingPlanToSubmit || !selectedVariant) return;

    const fireAddToCartEvent = (currentCart: CartDetails | null) => {
      if (!currentCart?.lines) return;

      const { id, lines, cost } = currentCart;

      const firstPrimaryItem =
        lines.find(
          (line) =>
            !!line.sellingPlanAllocation?.sellingPlan.id ||
            isOTPpouchVariant(line.merchandise.id, store) ||
            isOTPpacksVariantV53(line.merchandise.id, store)
        ) || lines[0];

      const mappedLines = lines.map((line) => {
        const itemId = Number(getIdFromShopifyFormattedId(line.merchandise.id));

        return {
          item_id: itemId,
          item_name: line.merchandise.product.title,
          item_brand: SHOPIFY_STORE_BRAND[store],
          item_variant: line.merchandise.title,
          variant_id: itemId,
          price: Number(line.cost.amountPerQuantity.amount),
          currency: line.cost.amountPerQuantity.currencyCode,
          quantity: line.quantity,
          flavor
        };
      });

      addToCartEvent({
        item_list_name: firstPrimaryItem.merchandise.product.title,
        item_list_id: firstPrimaryItem.merchandise.id,
        affiliation: utmString || pageSlug,
        items: mappedLines,
        eventId: id,
        store,
        currency: cost.subtotalAmount.currencyCode,
        value: cost.subtotalAmount.amount,
        deviceType: isMobileTabletDevice ? 'mobile' : 'desktop',
        isStandaloneProduct:
          lines.length === 1 && isStandaloneProduct(lines[0].merchandise.id, store)
      });
    };

    try {
      setCheckoutLoading(true);

      const subscriptionLines = getSubscriptionLineItems(
        selectedVariant,
        quantity,
        productCadence,
        isOtpProduct,
        flavor
      );

      // Get PDP-specific gifts to add alongside the main product
      const pdpGiftProducts = getGiftProducts();

      const cartInput: CartInput = {
        lines: [...subscriptionLines, ...pdpGiftProducts]
      };

      cartInput.attributes = [
        { key: 'checkoutOriginUrl', value: window.location.href },
        { key: CHECKOUT_TYPE.key, value: CHECKOUT_TYPE.PDP }
      ];

      if (utmString) {
        cartInput.attributes.push({
          key: 'utm-data',
          value: utmString
        });
      }

      if (session?.customer?.tags?.length) {
        cartInput.attributes.push({
          key: 'customerTags',
          value: JSON.stringify(session?.customer?.tags)
        });
      }

      const socialLadderTrackCode = cookies.get('sl-track-purchase');
      if (socialLadderTrackCode) {
        cartInput.attributes.push({
          key: 'social_ladder.tracking_cookie',
          value: socialLadderTrackCode
        });
      }

      selectProductEvent({
        store,
        itemId: variantIdToSubmit,
        itemName: selectedVariant.title,
        location: pageSlug,
        flavor,
        isStandaloneProduct: isStandaloneProduct(variantIdToSubmit, store)
      });

      // Always use addLinesToCart - it handles both new cart creation and adding to existing cart
      // This ensures cart rules are processed in both scenarios
      const cartData = await addLinesToCart(cartInput.lines, cartInput.attributes);

      if (!cartData) throw new Error('Failed to add items to cart');

      // Store the cart ID if it's a new cart
      if (cartData.id) {
        safeLocalStorage.setItem('cartId', cartData.id);
      }

      // PDP-specific gifts are now included in the initial cart input
      // This prevents the need for a second addLinesToCart call that was causing state conflicts
      fireAddToCartEvent(cartData);

      if (enableOpenCart) {
        openCart();
      } else {
        const cartUrl = await getShopifyCartUrl(store, cartData.id, locale);
        await router.push(cartUrl);
      }
    } catch (error) {
      const tags = {
        [MONITORING_TAGS.MARKETING]: true,
        [MONITORING_TAGS.CART]: true
      };
      datadogRum.addError(error, { tags });
    } finally {
      setCheckoutLoading(false);
    }
  }, [
    isOtpProduct,
    productCadence,
    selectedVariant,
    setCheckoutLoading,
    quantity,
    store,
    utmString,
    pageSlug,
    enableOpenCart,
    isMobileTabletDevice,
    flavor,
    addLinesToCart,
    openCart,
    getShopifyCartUrl,
    locale,
    router,
    session?.customer?.tags,
    getGiftProducts
  ]);

  return { handleCreatePdpCheckout };
};
